import React from "react";
import {
  Box, Typography, TextField, MenuItem, Slider, Button, Divider,
  Autocomplete, Switch, FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { NAVY, TEXT_SECONDARY } from "../../../theme/tokens";
import { DesignerZone, WIDGET_LABELS, WIDGET_TYPES, WidgetType, defaultConfigFor, editorWidgetKind } from "./types";
import WebsiteInspector from "./WebsiteInspector";
import MapInspector from "./MapInspector";
import EmbedInspector from "./EmbedInspector";
import WeatherInspector from "./WeatherInspector";
import MediaField from "./MediaField";
import { zoneMediaId } from "./mediaUtils";
import { IMedia } from "../../../Api/Interface/api.interface";

/**
 * Text animations — values match the shared TextWidget (LayoutRenderer), so the
 * same config plays identically in the editor, Preview and the device player.
 */
const TEXT_ANIMATIONS = [
  { value: "none", label: "None" },
  { value: "fade_in", label: "Fade In" },
  { value: "slide_left", label: "Slide Left" },
  { value: "slide_right", label: "Slide Right" },
  { value: "slide_up", label: "Slide Up" },
  { value: "slide_down", label: "Slide Down" },
  { value: "zoom_in", label: "Zoom In" },
  { value: "typewriter", label: "Typewriter" },
  { value: "marquee_left", label: "Marquee Left" },
  { value: "marquee_right", label: "Marquee Right" },
];
const DURATION_PRESETS = [0.5, 1, 2, 3, 5];

/** Hosted-video PAGE links (not directly playable in a <video> element). */
const isVideoPageUrl = (url: string): boolean =>
  /(?:youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com)/i.test(url);
/** A usable direct video source: http(s) and not a hosted-service page. */
const isPlayableVideoUrl = (url: string): boolean =>
  /^https?:\/\/\S+/i.test(url.trim()) && !isVideoPageUrl(url);

/**
 * Common IANA timezones for the clock picker (searchable, but free entry is
 * allowed so any valid IANA zone works). IANA — never "EST"/"PST" — so DST is
 * handled correctly by Intl in the renderer.
 */
const IANA_TIMEZONES = [
  "America/Toronto", "America/Vancouver", "America/Edmonton", "America/Winnipeg",
  "America/Halifax", "America/St_Johns", "America/Regina",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Dubai",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney", "UTC",
];

/**
 * Right-rail inspector. Edits the currently-selected zone in place.
 * No autosave — the page-level Save button persists the whole tree.
 */
interface InspectorProps {
  zone: DesignerZone | null;
  onChange: (patch: Partial<DesignerZone>) => void;
  onDelete: () => void;
  interacting?: boolean;
  onToggleInteract?: () => void;
  /** Company the template belongs to — scopes the media library + uploads. */
  companyId?: number;
  /** Resolved media records keyed by id, for image/video previews. */
  mediaById?: Record<number, IMedia>;
  /** Bubble a just-picked/uploaded media up so it resolves immediately (the
   *  page media query doesn't auto-refetch after a non-RTK upload). */
  onMediaResolved?: (m: IMedia) => void;
}

const Inspector: React.FC<InspectorProps> = ({
  zone, onChange, onDelete, interacting = false, onToggleInteract, companyId, mediaById = {},
  onMediaResolved,
}) => {
  if (!zone) {
    return (
      <Box sx={{ p: 2, color: TEXT_SECONDARY, fontSize: 13 }}>
        Select a zone on the canvas to edit it, or add a widget from the palette on the left.
      </Box>
    );
  }

  const cfg = zone.widget_config as Record<string, any>;
  const setCfg = (patch: Record<string, any>) =>
    onChange({ widget_config: { ...cfg, ...patch } });

  // The editor "kind" derives the panel + Widget-selector value from the zone's
  // type AND (for legacy website zones) its display_mode, so old templates open
  // in the right inspector without their stored record being rewritten.
  const kind = editorWidgetKind(zone);

  // Geometry (percent). Manual X/Y/W/H entry is clamped so a zone can never
  // leave the canvas, go negative, or fall below the minimum size — while a
  // full-canvas zone (0/0/100/100) is still reachable.
  const setGeom = (patch: Partial<{ x: number; y: number; w: number; h: number }>) => {
    const merged = { x: zone.x, y: zone.y, w: zone.w, h: zone.h, ...patch };
    const w = clampNum(merged.w, MIN_ZONE_PCT, 100);
    const h = clampNum(merged.h, MIN_ZONE_PCT, 100);
    onChange({
      w, h,
      x: clampNum(merged.x, 0, 100 - w),
      y: clampNum(merged.y, 0, 100 - h),
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY,
        textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
        Zone
      </Typography>

      <TextField
        size="small" fullWidth label="Name (optional)"
        value={zone.name ?? ""}
        onChange={(e) => onChange({ name: e.target.value || null })}
        sx={{ mb: 1.5 }}
      />

      <TextField
        select size="small" fullWidth label="Widget"
        value={kind}
        onChange={(e) => {
          const t = e.target.value as WidgetType;
          onChange({ widget_type: t, widget_config: defaultConfigFor(t) });
        }}
        sx={{ mb: 2 }}
      >
        {WIDGET_TYPES.map((t) => (
          <MenuItem key={t} value={t}>{WIDGET_LABELS[t]}</MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
        <NumberField label="X %" value={zone.x} min={0} onChange={(v) => setGeom({ x: v })} />
        <NumberField label="Y %" value={zone.y} min={0} onChange={(v) => setGeom({ y: v })} />
        <NumberField label="W %" value={zone.w} min={MIN_ZONE_PCT} onChange={(v) => setGeom({ w: v })} />
        <NumberField label="H %" value={zone.h} min={MIN_ZONE_PCT} onChange={(v) => setGeom({ h: v })} />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mb: 0.5 }}>
          Layer (z-order)
        </Typography>
        <Slider
          size="small" min={0} max={20} step={1} valueLabelDisplay="auto"
          value={zone.z_order}
          onChange={(_, v) => onChange({ z_order: v as number })}
        />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY,
        textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
        Widget settings
      </Typography>

      {/* Per-widget property panels. Image/Video reference media_id;
          most other widgets are pure-config. */}
      {kind === "image" && (
        <>
          <MediaField
            kind="image"
            mediaId={zoneMediaId(zone)}
            media={mediaById[zoneMediaId(zone) ?? -1]}
            companyId={companyId}
            onPick={(m) => { setCfg({ media_id: m.id }); onMediaResolved?.(m); }}
            onRemove={() => setCfg({ media_id: null })}
          />
          <TextField
            select size="small" fullWidth label="Fit"
            value={cfg.fit ?? "cover"}
            onChange={(e) => setCfg({ fit: e.target.value })}
          >
            <MenuItem value="cover">Cover (fill, crop)</MenuItem>
            <MenuItem value="contain">Contain (letterbox)</MenuItem>
            <MenuItem value="fill">Fill (stretch)</MenuItem>
          </TextField>
        </>
      )}

      {kind === "video" && (
        <>
          <TextField
            select size="small" fullWidth label="Video source"
            value={cfg.source === "url" ? "url" : "library"}
            onChange={(e) => setCfg({ source: e.target.value })}
            sx={{ mb: 1.5 }}
          >
            <MenuItem value="library">Media Library</MenuItem>
            <MenuItem value="url">Video URL</MenuItem>
          </TextField>

          {cfg.source === "url" ? (
            <TextField
              size="small" fullWidth label="Video URL" sx={{ mb: 1.5 }}
              placeholder="https://example.com/clip.mp4"
              value={cfg.video_url ?? ""}
              onChange={(e) => setCfg({ video_url: e.target.value })}
              error={!!cfg.video_url && !isPlayableVideoUrl(cfg.video_url)}
              helperText={
                !cfg.video_url
                  ? "Enter a direct .mp4 / .webm URL."
                  : isVideoPageUrl(cfg.video_url)
                    ? "YouTube/Vimeo page links aren't supported — use a direct video file URL."
                    : !isPlayableVideoUrl(cfg.video_url)
                      ? "Use a full http(s) URL to a video file."
                      : "Direct video URL."
              }
            />
          ) : (
            <MediaField
              kind="video"
              mediaId={zoneMediaId(zone)}
              media={mediaById[zoneMediaId(zone) ?? -1]}
              companyId={companyId}
              onPick={(m) => { setCfg({ media_id: m.id }); onMediaResolved?.(m); }}
              onRemove={() => setCfg({ media_id: null })}
            />
          )}
          <TextField
            select size="small" fullWidth label="Loop"
            value={cfg.loop ? "yes" : "no"}
            onChange={(e) => setCfg({ loop: e.target.value === "yes" })}
            sx={{ mb: 1 }}
          >
            <MenuItem value="yes">Loop</MenuItem>
            <MenuItem value="no">Play once</MenuItem>
          </TextField>
          <TextField
            select size="small" fullWidth label="Audio"
            value={cfg.muted ? "muted" : "audible"}
            onChange={(e) => setCfg({ muted: e.target.value === "muted" })}
            sx={{ mb: 1 }}
          >
            <MenuItem value="muted">Muted</MenuItem>
            <MenuItem value="audible">With audio</MenuItem>
          </TextField>
          <TextField
            select size="small" fullWidth label="Fit"
            value={cfg.fit ?? "cover"}
            onChange={(e) => setCfg({ fit: e.target.value })}
          >
            <MenuItem value="cover">Cover (fill, crop)</MenuItem>
            <MenuItem value="contain">Contain (letterbox)</MenuItem>
            <MenuItem value="fill">Fill (stretch)</MenuItem>
          </TextField>
        </>
      )}

      {kind === "text" && (
        <>
          <TextField
            size="small" fullWidth label="Content" multiline minRows={2}
            value={cfg.content ?? ""}
            onChange={(e) => setCfg({ content: e.target.value })}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
            <TextField size="small" label="Size (px)" type="number"
              value={cfg.font_size ?? 48}
              onChange={(e) => setCfg({ font_size: Number(e.target.value) })} />
            <TextField size="small" label="Weight" type="number"
              value={cfg.weight ?? 700}
              onChange={(e) => setCfg({ weight: Number(e.target.value) })} />
          </Box>
          <TextField
            size="small" fullWidth label="Color" type="color"
            value={cfg.color ?? "#FFFFFF"}
            onChange={(e) => setCfg({ color: e.target.value })}
            sx={{ mb: 1 }}
          />
          <TextField
            select size="small" fullWidth label="Align"
            value={cfg.align ?? "center"}
            onChange={(e) => setCfg({ align: e.target.value })}
            sx={{ mb: 1 }}
          >
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </TextField>

          {/* Animation — pure CSS in the shared renderer, so it plays the same
              in the editor, Preview and on the device player. */}
          <TextField
            select size="small" fullWidth label="Animation"
            value={cfg.animation ?? "none"}
            onChange={(e) => setCfg({ animation: e.target.value })}
            sx={{ mb: 1 }}
          >
            {TEXT_ANIMATIONS.map((a) => (
              <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
            ))}
          </TextField>

          {(cfg.animation === "marquee_left" || cfg.animation === "marquee_right") ? (
            <TextField
              size="small" fullWidth type="number" label="Marquee speed (seconds per loop)"
              helperText="Lower = faster"
              inputProps={{ min: 2, max: 120, step: 1 }}
              value={cfg.marquee_speed ?? 12}
              onChange={(e) => setCfg({ marquee_speed: Number(e.target.value) })}
            />
          ) : cfg.animation && cfg.animation !== "none" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
              <TextField
                select size="small" label="Duration"
                value={DURATION_PRESETS.includes(Number(cfg.animation_duration ?? 1)) ? String(cfg.animation_duration ?? 1) : "custom"}
                onChange={(e) => {
                  if (e.target.value === "custom") { setCfg({ animation_duration: Number(cfg.animation_duration ?? 1) }); }
                  else setCfg({ animation_duration: Number(e.target.value) });
                }}
              >
                {DURATION_PRESETS.map((d) => (
                  <MenuItem key={d} value={String(d)}>{d} sec</MenuItem>
                ))}
                <MenuItem value="custom">Custom…</MenuItem>
              </TextField>
              {!DURATION_PRESETS.includes(Number(cfg.animation_duration ?? 1)) && (
                <TextField
                  size="small" type="number" label="Custom (sec)"
                  inputProps={{ min: 0.1, max: 60, step: 0.1 }}
                  value={cfg.animation_duration ?? 1}
                  onChange={(e) => setCfg({ animation_duration: Number(e.target.value) })}
                />
              )}
              <TextField
                select size="small" label="Repeat"
                value={cfg.animation_repeat ?? "once"}
                onChange={(e) => setCfg({ animation_repeat: e.target.value })}
              >
                <MenuItem value="once">Once</MenuItem>
                <MenuItem value="loop">Loop</MenuItem>
              </TextField>
            </Box>
          ) : null}
        </>
      )}

      {kind === "clock" && (
        <>
          <TextField
            select size="small" fullWidth label="Time format"
            value={cfg.format ?? "HH:mm"}
            onChange={(e) => setCfg({ format: e.target.value })}
            sx={{ mb: 1 }}
          >
            <MenuItem value="HH:mm">24-hour (14:30)</MenuItem>
            <MenuItem value="HH:mm:ss">24-hour + seconds (14:30:05)</MenuItem>
            <MenuItem value="hh:mm a">12-hour (02:30 PM)</MenuItem>
            <MenuItem value="hh:mm:ss a">12-hour + seconds (02:30:05 PM)</MenuItem>
          </TextField>
          <TextField
            size="small" fullWidth type="number" label="Font size (%)"
            helperText="Scales the auto-fitted clock. 100 = default."
            inputProps={{ min: 10, max: 300, step: 5 }}
            value={cfg.font_scale ?? 100}
            onChange={(e) => setCfg({ font_scale: Number(e.target.value) })}
            sx={{ mb: 1 }}
          />
          <Autocomplete
            freeSolo
            size="small"
            options={IANA_TIMEZONES}
            value={cfg.timezone ?? ""}
            onChange={(_, v) => setCfg({ timezone: v || "" })}
            onInputChange={(_, v) => setCfg({ timezone: v })}
            renderInput={(params) => (
              <TextField {...params} label="Timezone (IANA)"
                helperText="DST handled automatically. Blank = device/browser timezone."
                placeholder="e.g. America/Toronto" />
            )}
          />
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Switch size="small" checked={!!cfg.show_date}
              onChange={(e) => setCfg({ show_date: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 12 }}>Show date</Typography>}
          />
          {cfg.show_date && (
            <TextField select size="small" fullWidth label="Date format"
              value={cfg.date_format ?? "short"}
              onChange={(e) => setCfg({ date_format: e.target.value })}
            >
              <MenuItem value="short">Tue, Aug 25</MenuItem>
              <MenuItem value="long">August 25, 2026</MenuItem>
              <MenuItem value="iso">2026-08-25</MenuItem>
            </TextField>
          )}
        </>
      )}

      {kind === "weather" && (
        <WeatherInspector cfg={cfg} setCfg={setCfg} />
      )}

      {kind === "qr" && (
        <>
          <TextField size="small" fullWidth label="Content / URL"
            value={cfg.content ?? ""}
            onChange={(e) => setCfg({ content: e.target.value })}
            sx={{ mb: 1 }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField size="small" type="color" label="Foreground"
              value={cfg.fg ?? "#000000"}
              onChange={(e) => setCfg({ fg: e.target.value })} />
            <TextField size="small" type="color" label="Background"
              value={cfg.bg ?? "#FFFFFF"}
              onChange={(e) => setCfg({ bg: e.target.value })} />
          </Box>
        </>
      )}

      {kind === "website" && (
        <WebsiteInspector
          cfg={cfg}
          setCfg={setCfg}
          interacting={interacting}
          onToggleInteract={onToggleInteract ?? (() => {})}
        />
      )}

      {kind === "iframe_map" && (
        <MapInspector
          cfg={cfg}
          setCfg={setCfg}
          interacting={interacting}
          onToggleInteract={onToggleInteract ?? (() => {})}
        />
      )}

      {kind === "iframe_embed" && (
        <EmbedInspector
          cfg={cfg}
          setCfg={setCfg}
          interacting={interacting}
          onToggleInteract={onToggleInteract ?? (() => {})}
        />
      )}

      {kind === "menu_board" && (
        <>
          <TextField select size="small" fullWidth label="Source"
            value={cfg.source ?? "products"}
            onChange={(e) => setCfg({ source: e.target.value })}
            sx={{ mb: 1 }}>
            <MenuItem value="products">Products</MenuItem>
            <MenuItem value="vmpos">VM POS menu</MenuItem>
            <MenuItem value="manual">Manual list</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label="Columns" type="number"
            value={cfg.columns ?? 2}
            onChange={(e) => setCfg({ columns: Number(e.target.value) })}
          />
        </>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Button
        fullWidth color="error" startIcon={<DeleteIcon />}
        onClick={onDelete}
      >
        Remove zone
      </Button>
    </Box>
  );
};

/** Minimum zone width/height as a percentage of the canvas. */
const MIN_ZONE_PCT = 5;

const clampNum = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, Number.isFinite(v) ? v : lo));

const NumberField: React.FC<{ label: string; value: number; min?: number; onChange: (v: number) => void }> = ({
  label, value, min = 0, onChange,
}) => (
  <TextField
    size="small" label={label} type="number"
    inputProps={{ step: 0.5, min, max: 100 }}
    value={Number.isFinite(value) ? value : 0}
    onChange={(e) => {
      const n = Number(e.target.value);
      if (Number.isFinite(n)) onChange(n);
    }}
  />
);

export default Inspector;
